import { useEffect, useMemo, useState } from "react";
import {
  AuthStatusOptionModel,
  AuthStatusOptionModelResponse,
} from "../data-model/authStatusOptions";
import { FirestoreService } from "../../shared/api/firebase/firestoreService";
import { FirestoreDocumentReference } from "../../shared/api/firebase/references";

interface AuthStatusOption {
  label: string;
  value: string;
}

interface UseAuthStatusFilterReturn {
  selectedAuthStatusFilter: string[];
  handleAuthStatusFilterChange: (selectedData: { value: string }) => void;
  handleRemoveAuthStatusFilter: (valueToRemove: string) => void;
  getAllAuthStatusOptions: AuthStatusOption[];
  getAvailableAuthStatusOptions: AuthStatusOption[];
  authStatusFilterPlaceholder: string;
  hasNoOptionsAvailable: boolean;
}

/**
 * useAuthStatusFilter
 *
 * Custom hook that manages auth status filter selection logic for facility forms.
 * This hook:
 * - Manages the selected auth status filter values
 * - Provides handlers for adding and removing filters
 * - Computes available options (excluding already selected ones)
 * - Provides a dynamic placeholder based on available options
 * - Fetches facility-specific auth status options directly from Firestore
 *
 * @param {string[]} initialValues - Initial auth status filter values (optional)
 * @param {string} facilityId - Facility ID to fetch auth status options for (optional, only for edit mode)
 * @returns {UseAuthStatusFilterReturn} Return object containing:
 * @returns {string[]} return.selectedAuthStatusFilter - Currently selected auth status filter values
 * @returns {(selectedData: { value: string }) => void} return.handleAuthStatusFilterChange - Function to add a new filter
 * @returns {(valueToRemove: string) => void} return.handleRemoveAuthStatusFilter - Function to remove a filter
 * @returns {AuthStatusOption[]} return.getAllAuthStatusOptions - All available auth status options (facility-specific)
 * @returns {AuthStatusOption[]} return.getAvailableAuthStatusOptions - Available options (excluding selected ones)
 * @returns {string} return.authStatusFilterPlaceholder - Placeholder text for the dropdown
 */
export const useAuthStatusFilter = (
  initialValues?: string[],
  facilityId?: string,
): UseAuthStatusFilterReturn => {
  const [selectedAuthStatusFilter, setSelectedAuthStatusFilter] = useState<
    string[]
  >(initialValues ?? []);
  const [facilityAuthStatusOptions, setFacilityAuthStatusOptions] = useState<
    AuthStatusOptionModel[]
  >([]);

  // Sync with initial values when they change
  useEffect(() => {
    if (initialValues !== undefined) {
      setSelectedAuthStatusFilter(initialValues);
    }
  }, [initialValues]);

  // Fetch facility-specific auth status options from Firestore
  useEffect(() => {
    if (facilityId && facilityId.trim() !== "") {
      FirestoreService.getDocument<AuthStatusOptionModelResponse>(
        FirestoreDocumentReference.authStatusOptions(facilityId),
      )
        .then((response) => {
          setFacilityAuthStatusOptions(response.data || []);
        })
        .catch((error) => {
          console.error("Error fetching auth status options:", error);
          setFacilityAuthStatusOptions([]);
        });
    } else {
      setFacilityAuthStatusOptions([]);
    }
  }, [facilityId]);

  // Get all available auth status options for the dropdown (facility-specific)
  const getAllAuthStatusOptions = useMemo(() => {
    // Return empty array if no facility-specific options are found
    if (!facilityAuthStatusOptions || facilityAuthStatusOptions.length === 0) {
      return [];
    }

    return facilityAuthStatusOptions
      .map((option) => ({
        label: option.text,
        value: option.text,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [facilityAuthStatusOptions]);

  // Filter out already selected options from dropdown
  const getAvailableAuthStatusOptions = useMemo(() => {
    return getAllAuthStatusOptions.filter(
      (option) => !selectedAuthStatusFilter.includes(option.value),
    );
  }, [getAllAuthStatusOptions, selectedAuthStatusFilter]);

  const authStatusFilterPlaceholder = useMemo(() => {
    if (getAvailableAuthStatusOptions.length === 0) {
      return "-";
    }
    return "Select an auth status ...";
  }, [getAvailableAuthStatusOptions.length]);

  // Check if no options are available from the facility (vs all options selected)
  const hasNoOptionsAvailable = useMemo(() => {
    return getAllAuthStatusOptions.length === 0;
  }, [getAllAuthStatusOptions.length]);

  const handleAuthStatusFilterChange = (selectedData: { value: string }) => {
    const { value } = selectedData;
    if (value && !selectedAuthStatusFilter.includes(value)) {
      setSelectedAuthStatusFilter((prev) => [...prev, value]);
    }
  };

  const handleRemoveAuthStatusFilter = (valueToRemove: string) => {
    setSelectedAuthStatusFilter((prev) =>
      prev.filter((value) => value !== valueToRemove),
    );
  };

  return {
    selectedAuthStatusFilter,
    handleAuthStatusFilterChange,
    handleRemoveAuthStatusFilter,
    getAllAuthStatusOptions,
    getAvailableAuthStatusOptions,
    authStatusFilterPlaceholder,
    hasNoOptionsAvailable,
  };
};
